import React, { useMemo, useState } from 'react';
import { MapPin, CheckCircle, AlertTriangle, XCircle, Route as RouteIcon, Sparkles, Clock, Navigation2, RotateCcw, X, Eye, EyeOff, Home } from 'lucide-react';
import { ProposalOpportunity } from './ProposalWizard';
import { Card, CardContent, CardFooter } from './ui/Card';
interface ZonePin {
  id: string;
  label: string;
  x: number; // percent 0-100
  y: number; // percent 0-100
  checkoutTime: string;
  checkinTime: string;
  durationMin: number;
  isBase?: boolean;
}
interface Suggestion {
  id: string;
  name: string;
  zone: string;
  window: string;
  pinIds: string[];
  suggestedUnitPrice: number;
}
interface BookedJob {
  id: string;
  label: string;
  x: number;
  y: number;
  time: string;
}
// Already-booked jobs the cleaner manager has, per date. Contextual only.
const bookedJobsByDate: Record<string, BookedJob[]> = {
  '24-gen': [{
    id: 'b1',
    label: 'Via Savona 18',
    x: 22,
    y: 70,
    time: '08:30'
  }, {
    id: 'b2',
    label: 'Largo Cairoli 2',
    x: 54,
    y: 20,
    time: '13:00'
  }],
  '25-gen': [{
    id: 'b3',
    label: 'Via Solari 9',
    x: 18,
    y: 48,
    time: '09:00'
  }],
  '27-gen': [{
    id: 'b4',
    label: 'Via Savona 18',
    x: 22,
    y: 70,
    time: '08:30'
  }, {
    id: 'b5',
    label: 'Corso Como 4',
    x: 78,
    y: 18,
    time: '11:30'
  }, {
    id: 'b6',
    label: 'Via Spadari 6',
    x: 60,
    y: 44,
    time: '15:30'
  }]
};
const pins: ZonePin[] = [{
  id: 'p1',
  label: 'Via Tortona 5',
  x: 30,
  y: 58,
  checkoutTime: '10:00',
  checkinTime: '15:00',
  durationMin: 60
}, {
  id: 'p2',
  label: 'Via Vigevano 12',
  x: 40,
  y: 72,
  checkoutTime: '10:30',
  checkinTime: '16:00',
  durationMin: 75
}, {
  id: 'p3',
  label: 'Corso Genova 3',
  x: 48,
  y: 50,
  checkoutTime: '11:00',
  checkinTime: '15:30',
  durationMin: 60
}, {
  id: 'p4',
  label: 'Navigli · Ripa 8',
  x: 38,
  y: 40,
  checkoutTime: '10:00',
  checkinTime: '16:00',
  durationMin: 90
}, {
  id: 'p5',
  label: 'Porta Romana 21',
  x: 66,
  y: 64,
  checkoutTime: '11:00',
  checkinTime: '15:00',
  durationMin: 60
}, {
  id: 'p6',
  label: 'Isola · Via Borsieri',
  x: 72,
  y: 26,
  checkoutTime: '10:30',
  checkinTime: '15:30',
  durationMin: 75
}, {
  id: 'p7',
  label: 'Centro · Via Dante',
  x: 58,
  y: 34,
  checkoutTime: '11:00',
  checkinTime: '16:00',
  durationMin: 60
}];
const suggestions: Suggestion[] = [{
  id: 'sug-navigli',
  name: 'Aggregazione Navigli',
  zone: 'Milano · Navigli',
  window: 'Ven 24 Gen · 10:00–16:00',
  pinIds: ['p1', 'p2', 'p4', 'p3'],
  suggestedUnitPrice: 45
}, {
  id: 'sug-centro',
  name: 'Aggregazione Centro-Isola',
  zone: 'Milano · Centro',
  window: 'Sab 25 Gen · 10:30–16:00',
  pinIds: ['p7', 'p6', 'p5'],
  suggestedUnitPrice: 48
}, {
  id: 'sug-large',
  name: 'Giro completo zona',
  zone: 'Milano · Sud-Ovest',
  window: 'Lun 27 Gen · 09:00–18:00',
  pinIds: ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7'],
  suggestedUnitPrice: 40
}];
const WORKING_WINDOW_MIN = 600; // 08:00 - 18:00
const pinById = (id: string) => pins.find(p => p.id === id)!;
function travelMinutes(a: ZonePin, b: ZonePin) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  return Math.max(5, Math.round(dist * 0.85 / 5) * 5);
}
interface Feasibility {
  stops: number;
  cleaningMin: number;
  travelMin: number;
  totalMin: number;
  marginMin: number;
  verdict: 'fattibile' | 'stretti' | 'non_fattibile';
  tightLeg: number | null; // index of leg (0-based) with smallest buffer, if any
}
function computeFeasibility(routePins: ZonePin[]): Feasibility {
  const cleaningMin = routePins.reduce((s, p) => s + p.durationMin, 0);
  let travelMin = 0;
  let tightLeg: number | null = null;
  let smallestLeg = Infinity;
  for (let i = 0; i < routePins.length - 1; i++) {
    const t = travelMinutes(routePins[i], routePins[i + 1]);
    travelMin += t;
    if (t < smallestLeg) {
      smallestLeg = t;
    }
    if (t >= 25 && (tightLeg === null || t > smallestLeg)) {
      tightLeg = i;
    }
  }
  // tightLeg: the longest travel leg (most likely to cause tight timing)
  let longest = -1;
  let longestIdx: number | null = null;
  for (let i = 0; i < routePins.length - 1; i++) {
    const t = travelMinutes(routePins[i], routePins[i + 1]);
    if (t > longest) {
      longest = t;
      longestIdx = i;
    }
  }
  const totalMin = cleaningMin + travelMin;
  const marginMin = WORKING_WINDOW_MIN - totalMin;
  let verdict: Feasibility['verdict'] = 'fattibile';
  if (totalMin > WORKING_WINDOW_MIN) verdict = 'non_fattibile';else if (marginMin <= 60) verdict = 'stretti';
  return {
    stops: routePins.length,
    cleaningMin,
    travelMin,
    totalMin,
    marginMin,
    verdict,
    tightLeg: verdict === 'stretti' ? longestIdx : null
  };
}
function fmtMin(min: number) {
  const h = Math.floor(Math.abs(min) / 60);
  const m = Math.abs(min) % 60;
  const sign = min < 0 ? '-' : '';
  if (h === 0) return `${sign}${m}m`;
  return `${sign}${h}h${m > 0 ? ` ${m}m` : ''}`;
}
const verdictStyle: Record<Feasibility['verdict'], {
  label: string;
  cls: string;
  icon: React.ReactNode;
}> = {
  fattibile: {
    label: 'Fattibile',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle size={14} data-id="element-121" />
  },
  stretti: {
    label: 'Tempi stretti',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <AlertTriangle size={14} data-id="element-122" />
  },
  non_fattibile: {
    label: 'Non fattibile',
    cls: 'bg-red-50 text-red-600 border-red-200',
    icon: <XCircle size={14} data-id="element-123" />
  }
};
type MapMode = 'suggerite' | 'manuale';
interface CmZoneMapProps {
  onSendProposal: (opp: ProposalOpportunity) => void;
  dateKey?: string;
  dateLabel?: string;
}
export function CmZoneMap({
  onSendProposal,
  dateKey,
  dateLabel
}: CmZoneMapProps) {
  const [mode, setMode] = useState<MapMode>('suggerite');
  const [showBookedJobs, setShowBookedJobs] = useState(true);
  const bookedJobs = dateKey && bookedJobsByDate[dateKey] || [];
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>(suggestions[0].id);
  const [manualRoute, setManualRoute] = useState<string[]>([]);
  // Active route pin ids depending on mode
  const activeSuggestion = suggestions.find(s => s.id === selectedSuggestion);
  const routeIds = mode === 'suggerite' ? activeSuggestion?.pinIds ?? [] : manualRoute;
  const routePins = routeIds.map(pinById);
  const feasibility = useMemo(() => routePins.length >= 1 ? computeFeasibility(routePins) : null, [routeIds.join(',')]);
  const sequenceOf = (pinId: string) => {
    const idx = routeIds.indexOf(pinId);
    return idx === -1 ? null : idx + 1;
  };
  const togglePin = (pinId: string) => {
    if (mode !== 'manuale') return;
    setManualRoute(prev => prev.includes(pinId) ? prev.filter(id => id !== pinId) : [...prev, pinId]);
  };
  // Switch to manual mode pre-loaded with the current suggestion's stops, so
  // the user can fine-tune the CleanAI-suggested route by hand.
  const editSuggestionManually = () => {
    if (activeSuggestion) {
      setManualRoute(activeSuggestion.pinIds);
      setMode('manuale');
    }
  };
  const handleProposal = () => {
    if (mode === 'suggerite' && activeSuggestion && feasibility) {
      onSendProposal({
        id: activeSuggestion.id,
        title: activeSuggestion.name,
        zone: activeSuggestion.zone,
        type: 'gruppo',
        jobsCount: activeSuggestion.pinIds.length,
        window: activeSuggestion.window,
        distanceKm: Math.round(feasibility.travelMin / 12 * 10) / 10,
        suggestedUnitPrice: activeSuggestion.suggestedUnitPrice
      });
    } else if (mode === 'manuale' && feasibility && manualRoute.length >= 2) {
      onSendProposal({
        id: 'percorso-manuale',
        title: 'Percorso personalizzato',
        zone: 'Milano · zona selezionata',
        type: 'gruppo',
        jobsCount: manualRoute.length,
        window: '08:00–18:00',
        distanceKm: Math.round(feasibility.travelMin / 12 * 10) / 10,
        suggestedUnitPrice: 45
      });
    }
  };
  return <div className="flex flex-col lg:flex-row gap-4" data-id="element-124">
      {/* MAP */}
      <div className="flex-1 min-w-0" data-id="element-125">
        {/* Mode tabs + booked-jobs toggle */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-id="element-126">
          <div className="inline-flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-id="element-127">
            <button onClick={() => setMode('suggerite')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${mode === 'suggerite' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-128">
              <Sparkles size={14} data-id="element-129" />
              Suggerite da CleanAI
            </button>
            <button onClick={() => setMode('manuale')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${mode === 'manuale' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-130">
              <RouteIcon size={14} data-id="element-131" />
              Percorso manuale
            </button>
          </div>
          <button type="button" onClick={() => setShowBookedJobs(v => !v)} aria-pressed={showBookedJobs} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${showBookedJobs ? 'bg-navy/5 border-navy/20 text-navy' : 'bg-white border-gray-200 text-gray-500 hover:text-navy'}`} data-id="element-132">
            {showBookedJobs ? <Eye size={15} data-id="element-133" /> : <EyeOff size={15} data-id="element-134" />}
            <span className="flex items-center gap-1.5" data-id="element-135">
              <span className="w-2.5 h-2.5 rounded-full bg-navy inline-block" data-id="element-136" />
              Job prenotati ({bookedJobs.length})
            </span>
          </button>
        </div>

        <div className="relative w-full h-[420px] lg:h-[520px] rounded-xl border border-border overflow-hidden" style={{
        backgroundColor: '#F5F6FA',
        backgroundImage: 'linear-gradient(to right, rgba(11,20,55,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,20,55,0.05) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-137">
          {/* Faint roads for texture */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-138">
            <path d="M0 65 Q 30 55 50 60 T 100 50" stroke="rgba(11,20,55,0.06)" strokeWidth="3" fill="none" data-id="element-139" />
            <path d="M55 0 Q 50 40 60 70 T 50 100" stroke="rgba(11,20,55,0.06)" strokeWidth="3" fill="none" data-id="element-140" />
          </svg>

          {/* Route polyline */}
          {routePins.length >= 2 && <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-141">
              <polyline points={routePins.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#FF4080" strokeWidth="0.8" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="2 1.2" data-id="element-142" />
            </svg>}

          {/* Booked jobs (contextual, non-selectable) */}
          {showBookedJobs && bookedJobs.map(j => <div key={j.id} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{
          left: `${j.x}%`,
          top: `${j.y}%`
        }} data-id="element-143">
                <span className="relative flex items-center justify-center w-7 h-7 rounded-full border-2 border-white shadow-md bg-navy" data-id="element-144">
                  <Home size={13} className="text-white" data-id="element-145" />
                </span>
                <span className="absolute left-1/2 -translate-x-1/2 top-8 whitespace-nowrap text-[10px] font-medium text-navy bg-white/90 px-1.5 py-0.5 rounded border border-border opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-146">
                  {j.label} · {j.time}
                </span>
              </div>)}

          {/* Opportunity pins */}
          {pins.map(p => {
          const seq = sequenceOf(p.id);
          const inRoute = seq !== null;
          return <button key={p.id} type="button" onClick={() => togglePin(p.id)} aria-label={`${p.label}${inRoute ? `, tappa ${seq}` : ''}`} disabled={mode !== 'manuale'} className={`absolute -translate-x-1/2 -translate-y-1/2 group ${mode === 'manuale' ? 'cursor-pointer' : 'cursor-default'} focus:outline-none`} style={{
            left: `${p.x}%`,
            top: `${p.y}%`
          }} data-id="element-147">
                <span className={`relative flex items-center justify-center w-8 h-8 rounded-full border-2 border-white shadow-md transition-transform group-hover:scale-110 ${inRoute ? 'bg-coral ring-2 ring-coral/30' : 'bg-white'}`} data-id="element-148">
                  {inRoute ? <span className="text-[11px] font-bold text-white" data-id="element-149">
                      {seq}
                    </span> : <MapPin size={15} className="text-coral" data-id="element-150" />}
                </span>
                {/* Label */}
                <span className="absolute left-1/2 -translate-x-1/2 top-9 whitespace-nowrap text-[10px] font-medium text-navy bg-white/90 px-1.5 py-0.5 rounded border border-border opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-151">
                  {p.label}
                </span>
              </button>;
        })}

          {/* Edit-route button (top-right) — only in suggested mode */}
          {mode === 'suggerite' && activeSuggestion && <button type="button" onClick={editSuggestionManually} className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold bg-white/95 backdrop-blur-sm border border-border text-navy shadow-sm hover:bg-white hover:border-coral transition-colors" data-id="element-152">
              <RouteIcon size={15} className="text-coral" data-id="element-153" />
              Modifica percorso
            </button>}

          {/* Legend */}
          <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm border border-border rounded-lg px-3 py-2 text-[10px] text-muted-foreground space-y-1" data-id="element-154">
            {showBookedJobs && bookedJobs.length > 0 && <div className="flex items-center gap-1.5" data-id="element-155">
                <span className="w-3 h-3 rounded-full bg-navy inline-block" data-id="element-156" />
                Job prenotato
              </div>}
            <div className="flex items-center gap-1.5" data-id="element-157">
              <span className="w-3 h-3 rounded-full bg-coral inline-block" data-id="element-158" />
              Pulizia in zona
            </div>
          </div>
        </div>
        {mode === 'manuale' && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5" data-id="element-159">
            <Navigation2 size={13} data-id="element-160" />
            Tocca i pin sulla mappa per costruire il percorso. CleanAI calcola
            la fattibilità.
          </p>}
      </div>

      {/* SIDE PANEL */}
      <div className="lg:w-[330px] lg:flex-shrink-0 space-y-3" data-id="element-161">
        {mode === 'suggerite' ? <>
            <h3 className="text-sm font-semibold text-navy flex items-center gap-2" data-id="element-162">
              <Sparkles size={16} className="text-coral" data-id="element-163" />
              Aggregazioni suggerite
            </h3>
            {suggestions.map(s => {
          const sp = s.pinIds.map(pinById);
          const f = computeFeasibility(sp);
          const active = s.id === selectedSuggestion;
          const v = verdictStyle[f.verdict];
          return <button key={s.id} onClick={() => setSelectedSuggestion(s.id)} className={`w-full text-left bg-white border-2 rounded-xl p-3 transition-all ${active ? 'border-coral' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-164">
                  <div className="flex items-start justify-between gap-2 mb-1.5" data-id="element-165">
                    <span className="text-sm font-semibold text-foreground" data-id="element-166">
                      {s.name}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${v.cls}`} data-id="element-167">
                      {v.icon}
                      {v.label}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1" data-id="element-168">
                    <div className="flex items-center gap-1.5" data-id="element-169">
                      <MapPin size={12} data-id="element-170" />
                      {s.zone}
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-171">
                      <Clock size={12} data-id="element-172" />
                      {s.pinIds.length} pulizie · ~{fmtMin(f.totalMin)} totali
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-173">
                      <Navigation2 size={12} data-id="element-174" />
                      Tragitto ~{fmtMin(f.travelMin)} ·{' '}
                      {Math.round(f.travelMin / 12 * 10) / 10} km
                    </div>
                  </div>
                </button>;
        })}
            {activeSuggestion && feasibility && <FeasibilitySummary feasibility={feasibility} routePins={routePins} onProposal={handleProposal} data-id="element-175" />}
          </> : <>
            <div className="flex items-center justify-between" data-id="element-176">
              <h3 className="text-sm font-semibold text-navy flex items-center gap-2" data-id="element-177">
                <RouteIcon size={16} className="text-coral" data-id="element-178" />
                Il tuo percorso
              </h3>
              {manualRoute.length > 0 && <button onClick={() => setManualRoute([])} className="text-xs text-muted-foreground hover:text-navy flex items-center gap-1" data-id="element-179">
                  <RotateCcw size={12} data-id="element-180" />
                  Reset
                </button>}
            </div>
            {manualRoute.length === 0 ? <div className="bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center" data-id="element-181">
                <Navigation2 size={22} className="text-gray-300 mx-auto mb-2" data-id="element-182" />
                <p className="text-xs text-muted-foreground" data-id="element-183">
                  Seleziona i pin sulla mappa per creare il tuo percorso.
                </p>
              </div> : <>
                <ul className="space-y-2" data-id="element-184">
                  {routePins.map((p, i) => <li key={p.id} className="flex items-center gap-2.5 bg-white border border-border rounded-lg p-2.5" data-id="element-185">
                      <span className="w-6 h-6 rounded-full bg-coral text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-186">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1" data-id="element-187">
                        <div className="text-xs font-medium text-foreground truncate" data-id="element-188">
                          {p.label}
                        </div>
                        <div className="text-[10px] text-muted-foreground" data-id="element-189">
                          Pulizia ~{fmtMin(p.durationMin)}
                        </div>
                      </div>
                      <button onClick={() => togglePin(p.id)} aria-label={`Rimuovi ${p.label}`} className="p-1 text-gray-400 hover:text-red-500" data-id="element-190">
                        <X size={14} data-id="element-191" />
                      </button>
                    </li>)}
                </ul>
                {feasibility && <FeasibilitySummary feasibility={feasibility} routePins={routePins} onProposal={handleProposal} data-id="element-192" />}
              </>}
          </>}
      </div>
    </div>;
}
function FeasibilitySummary({
  feasibility,
  routePins,
  onProposal
}: {
  feasibility: Feasibility;
  routePins: ZonePin[];
  onProposal: () => void;
}) {
  const v = verdictStyle[feasibility.verdict];
  const canPropose = feasibility.verdict !== 'non_fattibile' && routePins.length >= 2;
  return <Card data-id="element-193">
      <CardContent className="space-y-3" data-id="element-194">
        <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${v.cls}`} data-id="element-195">
          {v.icon}
          {v.label}
        </div>

        {feasibility.verdict === 'stretti' && feasibility.tightLeg !== null && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-196">
            Margine ridotto: tra la tappa {feasibility.tightLeg + 1} e la{' '}
            {feasibility.tightLeg + 2} il tragitto è il più lungo. Resta solo{' '}
            {fmtMin(feasibility.marginMin)} di margine sulla giornata.
          </p>}
        {feasibility.verdict === 'non_fattibile' && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2" data-id="element-197">
            Il percorso supera la finestra di lavoro di{' '}
            {fmtMin(Math.abs(feasibility.marginMin))}. Rimuovi una tappa o
            dividilo in due giorni.
          </p>}

        <div className="grid grid-cols-2 gap-y-1.5 text-xs" data-id="element-198">
          <span className="text-muted-foreground" data-id="element-199">Tappe</span>
          <span className="text-right font-medium text-foreground" data-id="element-200">
            {feasibility.stops}
          </span>
          <span className="text-muted-foreground" data-id="element-201">Tempo pulizie</span>
          <span className="text-right font-medium text-foreground" data-id="element-202">
            {fmtMin(feasibility.cleaningMin)}
          </span>
          <span className="text-muted-foreground" data-id="element-203">Tempo tragitto</span>
          <span className="text-right font-medium text-foreground" data-id="element-204">
            {fmtMin(feasibility.travelMin)}
          </span>
          <span className="text-muted-foreground" data-id="element-205">Totale</span>
          <span className="text-right font-semibold text-foreground" data-id="element-206">
            {fmtMin(feasibility.totalMin)}
          </span>
          <span className="text-muted-foreground" data-id="element-207">Margine giornata</span>
          <span className={`text-right font-semibold ${feasibility.marginMin < 0 ? 'text-red-600' : feasibility.marginMin <= 60 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-208">
            {fmtMin(feasibility.marginMin)}
          </span>
        </div>
      </CardContent>

      <CardFooter data-id="element-209">
        <button onClick={onProposal} disabled={!canPropose} className={`w-full px-4 h-10 rounded-xl text-sm font-semibold transition-colors ${canPropose ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-210">
          Invia proposta
        </button>
      </CardFooter>
    </Card>;
}